import { format } from 'date-fns';
import { CalendarClock, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type AccountLoginCode, brandStore, loginCodeStore } from '@/stores';
import { AccountLoginCodeStatus } from '@/types';

function formatDateTime(value?: string) {
  if (!value) {
    return '-';
  }
  return format(new Date(value), 'yyyy-MM-dd HH:mm:ss');
}

function renderStatus(status: AccountLoginCodeStatus) {
  switch (status) {
    case AccountLoginCodeStatus.ACCOUNT_LOGIN_CODE_STATUS_ACTIVE:
      return <Badge variant="success">可用</Badge>;
    case AccountLoginCodeStatus.ACCOUNT_LOGIN_CODE_STATUS_DELETED:
      return <Badge variant="outline">已删除</Badge>;
    default:
      return <Badge variant="secondary">未指定</Badge>;
  }
}

interface LoginCodeTableProps {
  onEditExpiry: (loginCode: AccountLoginCode) => void;
  onClearExpiry: (loginCode: AccountLoginCode) => void;
  onDelete: (loginCode: AccountLoginCode) => void;
}

export const LoginCodeTable = observer(
  ({ onEditExpiry, onClearExpiry, onDelete }: LoginCodeTableProps) => {
    const columns: Column<AccountLoginCode>[] = [
      {
        id: 'displayName',
        header: '展示名',
        accessor: (row) => (
          <div className="min-w-[140px]">
            <p className="font-medium text-foreground">{row.displayName}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              App: {row.appId}
            </p>
          </div>
        ),
      },
      {
        id: 'userId',
        header: '用户ID',
        accessor: (row) => (
          <span className="font-mono text-sm">
            {row.userId || '首次登录后生成'}
          </span>
        ),
      },
      {
        id: 'codeMasked',
        header: '登录码',
        accessor: (row) => (
          <span className="font-mono text-sm font-medium">
            {row.codeMasked}
          </span>
        ),
      },
      {
        id: 'status',
        header: '状态',
        accessor: (row) => renderStatus(row.status),
      },
      {
        id: 'expiresAt',
        header: '过期时间',
        accessor: (row) => {
          if (!row.expiresAt) {
            return (
              <span className="text-emerald-700 font-medium">长期有效</span>
            );
          }
          const expired = new Date(row.expiresAt) < new Date();
          return (
            <span className={expired ? 'text-destructive font-medium' : ''}>
              {formatDateTime(row.expiresAt)}
            </span>
          );
        },
      },
      {
        id: 'lastUsedAt',
        header: '最近使用',
        accessor: (row) => formatDateTime(row.lastUsedAt),
      },
      {
        id: 'createdBy',
        header: '创建人',
        accessor: (row) => (
          <span className="font-mono text-sm">{row.createdBy || '-'}</span>
        ),
      },
      {
        id: 'createdAt',
        header: '创建时间',
        accessor: (row) => formatDateTime(row.createdAt),
      },
      {
        id: 'actions',
        header: '操作',
        width: 220,
        accessor: (row) => {
          const disabled =
            row.status ===
              AccountLoginCodeStatus.ACCOUNT_LOGIN_CODE_STATUS_DELETED ||
            !brandStore.activeBrandId;

          return (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEditExpiry(row)}
                disabled={disabled}
              >
                <CalendarClock className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onClearExpiry(row)}
                disabled={disabled || !row.expiresAt}
              >
                清空
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(row)}
                disabled={disabled || loginCodeStore.deleting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ];

    return (
      <DataTable
        columns={columns}
        data={loginCodeStore.loginCodes}
        loading={loginCodeStore.loading}
        emptyMessage="暂无登录码"
      />
    );
  },
);
